import React, { useEffect, useRef, useState } from 'react';
import GenericPageContainer from '../../components/agv-generic-page-container';
import SelectFilter from '../../components/agv-header-select';
import { faCircle, faFilter, faLink } from '@fortawesome/free-solid-svg-icons';
import InfiniteScroll from 'react-infinite-scroll-component';
import { STATUS_ENUM } from '../../components/agv-status/status-enum';
import { useSelector } from 'react-redux';
import useGetTourLogs from '../../api/tours/useGetTourLogs';
import moment from 'moment/moment';
import ReactDatePicker from 'react-datepicker';
import CustomDatePickerDashboard from '../../components/agv-header-date-picker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Status from '../../components/agv-status';
import GenericTableSkeleton from '../../components/agv-generic-table/genericTableSkeleton';
import SearchFilter from '../../components/agv-header-search';

export default function ApiLogsPage() {
  const features = useSelector((state) => state.features);
  const {
    logs,
    isLoading,
    refetch,
    fetchNextPage,
    hasMore,
    filters,
    updateFilter,
  } = useGetTourLogs();

  const [selectedLog, setSelectedLog] = useState(null);
  const jsonContainerRef = useRef(null);

  const handleLogClick = (log) => {
    if (selectedLog && selectedLog.id === log.id) {
      setSelectedLog(null);
    } else {
      setSelectedLog(log);
      if (jsonContainerRef.current) {
        jsonContainerRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'start',
        });
      }
    }
  };

  const endResult = Math.min(
    filters.limit + filters.offset,
    logs?.pagination_info?.total_items ?? 0
  );

  const onChangeDate = (dates) => {
    const [start, end] = dates;
    let startDate = start ? moment(new Date(start)).format('YYYY-MM-DD') : '';
    let endDate = end ? moment(new Date(end)).format('YYYY-MM-DD') : '';
    updateFilter('start_date', startDate);
    updateFilter('end_date', endDate);
  };

  return (
    <>
      <GenericPageContainer
        title={'Logs API Fleet'}
        subtitle={`${endResult} sur ${logs?.pagination_info?.total_items} au total`}
        filters={
          <>
            <div className="tw-relative">
              <ReactDatePicker
                selected={filters?.start_date}
                onChange={onChangeDate}
                customInput={<CustomDatePickerDashboard />}
                startDate={
                  filters.start_date ? new Date(filters.start_date) : null
                }
                endDate={filters.end_date ? new Date(filters.end_date) : null}
                selectsRange
                locale="fr"
                dateFormat="dd MMM yyyy"
              />
            </div>
            <SelectFilter
              value={filters.status}
              onChange={(value) => updateFilter('status', value)}
              options={[
                { value: '', name: 'Tous' },
                { value: 'DONE', name: 'Succès' },
                { value: 'ERRORED', name: 'Erreurs' },
                { value: 'CREATED', name: 'En attente de traitement' },
                { value: 'IN_PROGRESS', name: 'En cours' },
              ]}
              icon={faFilter}
            />
          </>
        }
      >
        <div className="tw-flex tw-space-x-4">
          <div
            className={`${selectedLog ? 'tw-w-1/2' : 'tw-w-full'} tw-my-8 tw-overflow-y-auto tw-rounded-md tw-bg-white`}
            id={'scrollableDiv'}
            style={{ height: '80vh' }}
          >
            <div className="tw-divide-solid">
              {isLoading && !logs ? (
                <GenericTableSkeleton />
              ) : (
                <InfiniteScroll
                  dataLength={logs?.items?.length ?? 0}
                  next={fetchNextPage}
                  hasMore={hasMore}
                  loader={<GenericTableSkeleton numberOfRows={1} />}
                  scrollableTarget="scrollableDiv"
                >
                  {logs?.items?.map((log) => (
                    <div
                      key={log.id}
                      className={`tw-flex tw-justify-between tw-items-center tw-p-3 tw-cursor-pointer ${
                        selectedLog?.id === log.id ? 'tw-bg-gray-200' : ''
                      }`}
                      onClick={() => handleLogClick(log)}
                    >
                      <div className="tw-flex tw-items-center tw-max-w-fit">
                        <FontAwesomeIcon
                          className={`tw-mr-2 tw-size-4`}
                          icon={faCircle}
                          color={
                            log.status === 'DONE'
                              ? 'rgb(0 183 95 / 0.55)'
                              : 'rgb(252 89 89 / 0.55)'
                          }
                        />
                        <div className="tw-max-w-xl">
                          <div className="tw-text-smi tw-font-work tw-leading-4 tw-mb-1">
                            {moment(log.created_at).format(
                              'D MMM YYYY [à] HH:mm'
                            ) +
                              ' - ' +
                              log?.datas?.DLA_TOURNE?.ID_TOURNEE}
                          </div>
                          <div className="tw-text-smi tw-font-work tw-text-text-dark-medium-importance tw-leading-4 tw-line-clamp-1">
                            {log?.last_error_if_any?.message}
                          </div>
                        </div>
                      </div>
                      <div className="tw-flex tw-items-center tw-min-w-fit">
                        <Status
                          status={
                            log.status === 'DONE'
                              ? STATUS_ENUM.POSITIVE
                              : STATUS_ENUM.NEGATIVE
                          }
                          text={log.status}
                        />
                      </div>
                    </div>
                  ))}
                </InfiniteScroll>
              )}
            </div>
          </div>
          <div
            className={`${selectedLog ? 'tw-w-1/2' : 'tw-hidden'} tw-bg-gray-100 tw-p-4 tw-overflow-y-auto tw-rounded-md tw-my-8`}
            style={{ height: '80vh' }}
          >
            {selectedLog && (
              <div ref={jsonContainerRef}>
                {selectedLog?.last_error_if_any?.message ? (
                  <pre className="tw-text-red-400 tw-bg-red-100 tw-p-1 tw-rounded-sm tw-text-[13px] tw-font-semibold tw-whitespace-pre-wrap tw-break-words">
                    {selectedLog?.last_error_if_any?.message}
                  </pre>
                ) : (
                  <pre className="tw-text-green-400 tw-bg-green-100 tw-p-1 tw-rounded-sm tw-text-[13px] tw-font-semibold tw-whitespace-pre-wrap tw-break-words">
                    {'Succès - 200'}
                  </pre>
                )}
                <pre className="tw-text-darkgray-200 tw-text-[12px]">
                  {JSON.stringify(selectedLog.datas, null, 2)
                    .replace(
                      /"(\w+)"\s*:/g,
                      '<span class="tw-text-white">"$1"</span>:'
                    ) // Noms des objets en gris
                    .replace(
                      /:\s*"([^"]*)"/g,
                      ': <span class="tw-text-orange-300">"$1"</span>'
                    ) // Chaînes de caractères en rouge
                    .replace(
                      /:\s*(-?\d+(\.\d+)?)/g,
                      ': <span class="tw-text-blue-300">$1</span>'
                    ) // Valeurs numériques en bleu
                    .split('\n')
                    .map((line, index) => (
                      <div
                        key={index}
                        dangerouslySetInnerHTML={{ __html: line }}
                      />
                    ))}
                </pre>
              </div>
            )}
          </div>
        </div>
      </GenericPageContainer>
    </>
  );
}
